import React, { useEffect, useState } from 'react';
import {
  Container, Typography, Box, CircularProgress,
  Alert, Grid, TextField, Button, IconButton
} from '@mui/material';
import AddCircleIcon from '@mui/icons-material/AddCircle';
import DeleteIcon from '@mui/icons-material/Delete';
import { useParams, useNavigate } from 'react-router-dom';
import { fetchApi } from './fetchApi';


interface EinlagerungZeile {
  regalnummer: string;
  fachnummer: string;
  boxnummer: string;
  menge: number | '';
}

interface PositionDaten {
  position_id: number;
  artikel_id: number;
  sku: string;
  bezeichnung: string;
  menge_gefordert: number;
  einlagerungen: EinlagerungZeile[];
}

export default function LieferscheinEinlagern() {
  const { id } = useParams();
  const navigate = useNavigate();

  const [kopf, setKopf] = useState<any>(null);
  const [positionen, setPositionen] = useState<PositionDaten[]>([]);
  const [laden, setLaden] = useState(true);
  const [fehler, setFehler] = useState<string | null>(null);

  // ------------------------------------------------------
  // Daten laden
  // ------------------------------------------------------
  useEffect(() => {
    async function lade() {
      try {
        const res = await fetchApi(`/api/lieferschein/${id}/einlagern`);
        const json = await res.json();

        if (!res.ok) {
          setFehler(json.error || 'Fehler beim Laden der Daten');
          return;
        }

        setKopf(json.lieferschein);

        const pos = json.positionen.map((p: any) => ({
          ...p,
          einlagerungen: [
            { regalnummer: '', fachnummer: '', boxnummer: '', menge: '' }
          ]
        }));

        setPositionen(pos);

      } catch (err: any) {
  console.error(err);
  setFehler(
    err?.message === '401'
      ? 'Session abgelaufen. Bitte neu einloggen.'
      : 'Fehler beim Laden.'
  );
} finally {
  setLaden(false);
}

    }

    lade();
  }, [id]);

  // ------------------------------------------------------
  // Einlagerungszeile ändern
  // ------------------------------------------------------
  function updateZeile(
    posIndex: number,
    zeileIndex: number,
    field: keyof EinlagerungZeile,
    value: any
  ) {
    const copy = [...positionen];

    if (!copy[posIndex].einlagerungen[zeileIndex]) {
      copy[posIndex].einlagerungen[zeileIndex] = {
        regalnummer: '',
        fachnummer: '',
        boxnummer: '',
        menge: ''
      };
    }

    copy[posIndex].einlagerungen[zeileIndex][field] = value;
    setPositionen(copy);
  }

  function addZeile(posIndex: number) {
    const copy = [...positionen];
    copy[posIndex].einlagerungen.push({
      regalnummer: '',
      fachnummer: '',
      boxnummer: '',
      menge: ''
    });
    setPositionen(copy);
  }

  function removeZeile(posIndex: number, zeileIndex: number) {
    const copy = [...positionen];
    copy[posIndex].einlagerungen.splice(zeileIndex, 1);

    if (copy[posIndex].einlagerungen.length === 0) {
      copy[posIndex].einlagerungen.push({
        regalnummer: '',
        fachnummer: '',
        boxnummer: '',
        menge: ''
      });
    }

    setPositionen(copy);
  }

  // ------------------------------------------------------
  // Gesamte Einlagerung absenden
  // ------------------------------------------------------
  async function speichern() {
    // Strenge Prüfung: jede Position vollständig einlagern
    for (const pos of positionen) {
      const sum = pos.einlagerungen.reduce((acc, z) => acc + Number(z.menge || 0), 0);
      if (sum !== pos.menge_gefordert) {
        alert(
          `Position ${pos.sku}: Die Summe der Mengen (${sum}) muss exakt ${pos.menge_gefordert} sein.`
        );
        return;
      }
    }

    const payload = {
      einlagerungen: positionen.map(p => ({
        position_id: p.position_id,
        artikel_id: p.artikel_id,
        einlagerungen: p.einlagerungen.map(z => ({
          regalnummer: z.regalnummer,
          fachnummer: z.fachnummer,
          boxnummer: z.boxnummer,
          menge: Number(z.menge)
        }))
      }))
    };

    try {
      const res = await fetchApi(
  `/api/lieferschein/${id}/einlagern`,
  {
    method: 'POST',
    body: JSON.stringify(payload)
  }
);


      const json = await res.json();

      if (!res.ok) {
  alert(res.status === 401 ? 'Session abgelaufen. Bitte neu einloggen.' : (json.error || 'Fehler beim Einlagern'));
  return;
}


      alert('Wareneingang erfolgreich eingelagert!');
      navigate(`/lieferschein/${id}`);

    } catch (err) {
      alert('Fehler beim Speichern.');
      console.error(err);
    }
  }

  // ------------------------------------------------------
  // UI
  // ------------------------------------------------------
  if (laden) {
    return (
      <Container><Box mt={5} textAlign="center"><CircularProgress /></Box></Container>
    );
  }

  if (fehler || !kopf) {
    return (
      <Container>
        <Alert severity="error">{fehler}</Alert>
        <Button sx={{ mt: 2 }} variant="outlined" onClick={() => navigate('/lieferschein-liste')}>
          Zurück
        </Button>
      </Container>
    );
  }

  return (
    <Container sx={{ mt: 4, mb: 10 }}>
      <Box display="flex" justifyContent="space-between" mb={3}>
        <Typography variant="h5">
          Einlagern – {kopf.lieferschein_nr}
        </Typography>

        <Button variant="outlined" onClick={() => navigate(`/lieferschein/${id}`)}>
          Zurück
        </Button>
      </Box>

      {positionen.map((pos, posIndex) => (
        <Box key={pos.position_id} mb={4} p={2} border="1px solid #ccc" borderRadius="8px">
          <Typography variant="h6">
            {pos.sku} – {pos.bezeichnung}
          </Typography>
          <Typography variant="body2" sx={{ mb: 2 }}>
            Geforderte Menge: <strong>{pos.menge_gefordert}</strong>
          </Typography>

          {pos.einlagerungen.map((z, zeileIndex) => (
            <Grid container spacing={2} key={zeileIndex} sx={{ mb: 1 }}>

              <Grid item xs={3}>
                <TextField
                  label="Regalnummer"
                  fullWidth
                  value={z.regalnummer}
                  onChange={(e) =>
                    updateZeile(posIndex, zeileIndex, 'regalnummer', e.target.value)
                  }
                />
              </Grid>

              <Grid item xs={3}>
                <TextField
                  label="Fachnummer"
                  fullWidth
                  value={z.fachnummer}
                  onChange={(e) =>
                    updateZeile(posIndex, zeileIndex, 'fachnummer', e.target.value)
                  }
                />
              </Grid>

              <Grid item xs={3}>
                <TextField
                  label="Boxnummer"
                  fullWidth
                  value={z.boxnummer}
                  onChange={(e) =>
                    updateZeile(posIndex, zeileIndex, 'boxnummer', e.target.value)
                  }
                  required
                />
              </Grid>

              <Grid item xs={2}>
                <TextField
                  label="Menge"
                  type="number"
                  fullWidth
                  value={z.menge}
                  onChange={(e) =>
                    updateZeile(posIndex, zeileIndex, 'menge', Number(e.target.value))
                  }
                  required
                />
              </Grid>

              <Grid item xs={1}>
                <IconButton color="error" onClick={() => removeZeile(posIndex, zeileIndex)}>
                  <DeleteIcon />
                </IconButton>
              </Grid>

            </Grid>
          ))}

          <Button
            startIcon={<AddCircleIcon />}
            onClick={() => addZeile(posIndex)}
          >
            weitere Zeile
          </Button>

        </Box>
      ))}

      <Button
        variant="contained"
        color="success"
        size="large"
        onClick={speichern}
      >
        Einlagerung abschließen
      </Button>

    </Container>
  );
}
